import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Hash, Save, X, Check } from 'lucide-react';
import { collection, query, orderBy, limit, getDocs, addDoc, updateDoc, doc } from 'firebase/firestore';
import { db } from '../config/firebase';

const MatchSetup = ({ fixture, onSave, onClose }) => {
  const [courtNumber, setCourtNumber] = useState(fixture.court || '');
  const [matchNumber, setMatchNumber] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    generateMatchNumber();
  }, []);

  const generateMatchNumber = async () => {
    try {
      setLoading(true);
      
      // Get the next match number by counting existing matches
      const matchesQuery = query(
        collection(db, 'fixtures'),
        orderBy('createdAt', 'desc')
      );
      
      const matchesSnapshot = await getDocs(matchesQuery);
      const totalMatches = matchesSnapshot.docs.length;
      const nextMatchNumber = totalMatches + 1;
      
      setMatchNumber(nextMatchNumber);
      setLoading(false);
    } catch (err) {
      console.error('Error generating match number:', err);
      setMatchNumber(1); // Fallback
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      
      // Validate court number
      if (!courtNumber.trim()) {
        setError('Please enter a court number');
        return;
      }

      // Validate match number
      if (!matchNumber) {
        setError('Match number is required');
        return;
      }

      // Save court and match number to fixture
      await onSave({
        court: courtNumber.trim(),
        matchNumber: parseInt(matchNumber),
        updatedAt: new Date().toISOString()
      });
      
      onClose();
    } catch (err) {
      setError('Failed to save match setup: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const courtOptions = [
    'Court 1', 'Court 2', 'Court 3', 'Court 4', 'Court 5',
    'Court 6', 'Court 7', 'Court 8', 'Court 9', 'Court 10',
    'Main Court', 'Center Court', 'Court A', 'Court B', 'Court C'
  ];

  if (loading) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <div style={{
          background: 'white',
          borderRadius: '1rem',
          padding: '2rem',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '1.125rem', fontWeight: '600', color: '#1C563D' }}>
            Setting up match...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '1rem',
      overflowY: 'auto'
    }}>
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        style={{
          background: 'white',
          borderRadius: '1.5rem',
          padding: '1.5rem',
          maxWidth: '500px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          margin: 'auto'
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '2rem',
          paddingBottom: '1rem',
          borderBottom: '1px solid #E5E7EB'
        }}>
          <div>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1C563D', marginBottom: '0.5rem' }}>
              Match Setup
            </h2>
            <p style={{ fontSize: '0.875rem', color: '#6B7280' }}>
              {fixture.homeTeam} vs {fixture.awayTeam}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: '#F3F4F6',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: '#6B7280'
            }}
          >
            <X size={20} />
          </button>
        </div>

        {error && (
          <div style={{
            background: '#FEF2F2',
            border: '1px solid #FECACA',
            color: '#DC2626',
            padding: '0.75rem 1rem',
            borderRadius: '0.5rem',
            marginBottom: '1.5rem',
            fontSize: '0.875rem'
          }}>
            {error}
          </div>
        )}

        {/* Court Number Selection */}
        <div style={{ marginBottom: '2rem' }}>
          <label style={{ 
            display: 'block', 
            fontSize: '1rem', 
            fontWeight: '600', 
            color: '#374151', 
            marginBottom: '0.75rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <MapPin size={20} style={{ color: '#10B981' }} />
            Court Number
          </label>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', 
            gap: '0.5rem', 
            marginBottom: '1rem',
            maxHeight: '200px',
            overflowY: 'auto'
          }}>
            {courtOptions.map((court) => (
              <button
                key={court}
                onClick={() => setCourtNumber(court)}
                style={{
                  padding: '0.75rem',
                  border: courtNumber === court ? '2px solid #10B981' : '2px solid #E5E7EB',
                  borderRadius: '0.5rem',
                  background: courtNumber === court ? '#F0FDF4' : 'white',
                  color: courtNumber === court ? '#065F46' : '#374151',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                {courtNumber === court && <Check size={16} />}
                {court}
              </button>
            ))}
          </div>
          
          <input
            type="text"
            placeholder="Or enter custom court name..."
            value={courtNumber}
            onChange={(e) => setCourtNumber(e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '2px solid #E5E7EB',
              borderRadius: '0.5rem',
              fontSize: '0.875rem',
              background: 'white'
            }}
          />
        </div>

        {/* Match Number */}
        <div style={{ marginBottom: '2rem' }}>
          <label style={{ 
            display: 'block', 
            fontSize: '1rem', 
            fontWeight: '600', 
            color: '#374151', 
            marginBottom: '0.75rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <Hash size={20} style={{ color: '#3B82F6' }} />
            Match Number
          </label>
          
          <div style={{
            background: 'linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)',
            border: '1px solid #3B82F6',
            borderRadius: '0.75rem',
            padding: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <div style={{
              background: '#3B82F6',
              color: 'white',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.25rem',
              fontWeight: 'bold'
            }}>
              #{matchNumber}
            </div>
            <div>
              <div style={{ fontSize: '0.875rem', color: '#1E40AF', fontWeight: '600' }}>
                Auto-assigned Match Number
              </div>
              <div style={{ fontSize: '0.75rem', color: '#6B7280' }}>
                This will help track matches in the league
              </div>
            </div>
          </div>
        </div>

        {/* Match Info Summary */}
        <div style={{
          background: 'linear-gradient(135deg, #F9FAFB 0%, #F3F4F6 100%)',
          borderRadius: '0.75rem',
          padding: '1rem',
          marginBottom: '2rem',
          border: '1px solid #E5E7EB'
        }}>
          <h4 style={{ fontSize: '1rem', fontWeight: 'bold', color: '#374151', marginBottom: '0.75rem' }}>
            Match Summary
          </h4>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
            gap: '1rem', 
            fontSize: '0.875rem' 
          }}>
            <div>
              <div style={{ color: '#6B7280', marginBottom: '0.25rem' }}>Teams</div>
              <div style={{ fontWeight: '600', color: '#1F2937' }}>
                {fixture.homeTeam} vs {fixture.awayTeam}
              </div>
            </div>
            <div>
              <div style={{ color: '#6B7280', marginBottom: '0.25rem' }}>Court</div>
              <div style={{ fontWeight: '600', color: '#1F2937' }}>
                {courtNumber || 'Not selected'}
              </div>
            </div>
            <div>
              <div style={{ color: '#6B7280', marginBottom: '0.25rem' }}>Match #</div>
              <div style={{ fontWeight: '600', color: '#1F2937' }}>
                #{matchNumber}
              </div>
            </div>
            <div>
              <div style={{ color: '#6B7280', marginBottom: '0.25rem' }}>Date</div>
              <div style={{ fontWeight: '600', color: '#1F2937' }}>
                {new Date(fixture.date).toLocaleDateString()}
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div style={{ 
          display: 'flex', 
          gap: '1rem', 
          justifyContent: 'flex-end',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={onClose}
            disabled={saving}
            style={{
              padding: '0.75rem 1.5rem',
              background: '#F3F4F6',
              color: '#374151',
              border: 'none',
              borderRadius: '0.5rem',
              cursor: saving ? 'not-allowed' : 'pointer',
              fontWeight: '600',
              fontSize: '0.875rem',
              minWidth: '100px',
              flex: '1'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !courtNumber.trim()}
            style={{
              padding: '0.75rem 1.5rem',
              background: saving || !courtNumber.trim() ? '#9CA3AF' : 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '0.5rem',
              cursor: saving || !courtNumber.trim() ? 'not-allowed' : 'pointer',
              fontWeight: '600',
              fontSize: '0.875rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              minWidth: '120px',
              flex: '1'
            }}
          >
            {saving ? (
              <>
                <div style={{ width: '16px', height: '16px', border: '2px solid transparent', borderTop: '2px solid white', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                Saving...
              </>
            ) : (
              <>
                <Save size={16} />
                Start Match
              </>
            )}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default MatchSetup;
